/**
 * flip-animate — FLIP transition utilities for tile repositioning.
 *
 * FLIP = First, Last, Invert, Play.
 * 1. First: measure tile rects BEFORE the DOM update.
 * 2. Last: after the DOM update, measure again.
 * 3. Invert: apply transform to place tile at its old position visually.
 * 4. Play: transition to identity transform (~180ms ease-out).
 *
 * HARD RULES:
 * - prefers-reduced-motion → no animation at all.
 * - Never animate during active drag (ghost is the live feedback).
 * - No bounce/overshoot (calm brand). ease-out only.
 * - transform/opacity only (compositor thread = 60fps).
 * - Does not break crossfade classes from template swap.
 *
 * Immutability: no mutations to external state — DOM style writes are
 * transient visual effects, not data mutations.
 */

/** Returns true if prefers-reduced-motion is active. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Capture a snapshot of element rects keyed by element identity.
 * Call BEFORE the DOM update that causes the layout change.
 */
export function captureRects(elements: Map<string, HTMLElement>): Map<string, DOMRect> {
  const rects = new Map<string, DOMRect>();
  for (const [key, el] of elements) {
    rects.set(key, el.getBoundingClientRect());
  }
  return rects;
}

/**
 * Compute the inverted transforms (dx, dy) for each element given its old rect
 * and new rect. Pure math — no DOM access.
 *
 * @param oldRects - rects captured before the DOM update
 * @param newRects - rects captured after the DOM update
 * @returns Map<key, {dx: number, dy: number}> — inverted offsets to apply
 */
export function computeInvertedTransforms(
  oldRects: Map<string, DOMRect>,
  newRects: Map<string, DOMRect>,
): Map<string, { dx: number; dy: number }> {
  const result = new Map<string, { dx: number; dy: number }>();
  for (const [key, oldRect] of oldRects) {
    const newRect = newRects.get(key);
    if (!newRect) continue;
    const dx = oldRect.left - newRect.left;
    const dy = oldRect.top - newRect.top;
    // Only animate if there's actually movement
    if (dx !== 0 || dy !== 0) {
      result.set(key, { dx, dy });
    }
  }
  return result;
}

/**
 * Apply FLIP animation to elements that moved.
 *
 * After calling this, elements start at their old visual position (via transform)
 * and transition to their new position (identity transform) over ~180ms ease-out.
 *
 * No-ops if prefers-reduced-motion is active.
 * No-ops if invertedTransforms is empty.
 *
 * @param elements - Map<key, HTMLElement> of elements to animate
 * @param invertedTransforms - output of computeInvertedTransforms
 * @param durationMs - animation duration (default 180ms)
 */
export function playFlipAnimations(
  elements: Map<string, HTMLElement>,
  invertedTransforms: Map<string, { dx: number; dy: number }>,
  durationMs = 240,
  easing = 'var(--spring-settle)',
): void {
  if (prefersReducedMotion()) return;
  if (invertedTransforms.size === 0) return;

  for (const [key, { dx, dy }] of invertedTransforms) {
    const el = elements.get(key);
    if (!el) continue;
    // Step 3: Invert — apply the inverted transform (visually: put element where it was)
    el.style.transform = `translate(${dx}px, ${dy}px)`;
    el.style.transition = 'none';
    // Step 4: Play — on next frame, transition to identity
    // requestAnimationFrame ensures the browser has painted the inverted state first.
    requestAnimationFrame(() => {
      el.style.transition = `transform ${durationMs}ms ${easing}`;
      el.style.transform = '';
      // Clean up inline styles after animation completes
      const cleanup = () => {
        el.style.transition = '';
        el.style.transform = '';
        el.removeEventListener('transitionend', cleanup);
      };
      el.addEventListener('transitionend', cleanup, { once: true });
    });
  }
}
